import React from 'react';
import { motion, Variants, HTMLMotionProps } from 'motion/react';
import { cn } from '../../utils/cn';

interface AnimatedTextProps extends Omit<HTMLMotionProps<'span'>, 'children'> {
  /** The text content to animate */
  children: string;
  /** Animation type */
  animation?: 'fadeUp' | 'fadeIn' | 'slideUp' | 'typewriter' | 'wordByWord' | 'letterByLetter';
  /** Delay before animation starts (in seconds) */
  delay?: number;
  /** Duration of animation (in seconds) */
  duration?: number;
  /** Stagger delay between words/letters (in seconds) */
  staggerDelay?: number;
  /** Additional CSS classes */
  className?: string;
  /** Whether to use gradient text effect */
  gradient?: boolean;
  /** Whether animation should trigger on view */
  triggerOnView?: boolean;
  /** Once animation plays, should it replay on subsequent views? */
  repeatOnView?: boolean;
  /** HTML tag to render */
  as?: 'span' | 'p' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'div';
}

// Animation variants
const fadeUpVariants: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: (delay: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      delay,
      ease: [0.4, 0, 0.2, 1],
    },
  }),
};

const fadeInVariants: Variants = {
  hidden: { opacity: 0 },
  visible: (delay: number) => ({
    opacity: 1,
    transition: {
      duration: 0.5,
      delay,
      ease: [0.4, 0, 0.2, 1],
    },
  }),
};

const slideUpVariants: Variants = {
  hidden: { opacity: 0, y: 40 },
  visible: (delay: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      delay,
      ease: [0.175, 0.885, 0.32, 1.275],
    },
  }),
};

// Container for word-by-word animation
const wordContainerVariants: Variants = {
  hidden: {},
  visible: (custom: { staggerDelay: number; delay: number }) => ({
    transition: {
      staggerChildren: custom.staggerDelay,
      delayChildren: custom.delay,
    },
  }),
};

const wordVariants: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.4, 0, 0.2, 1],
    },
  },
};

// Container for letter-by-letter animation
const letterContainerVariants: Variants = {
  hidden: {},
  visible: (staggerDelay: number) => ({
    transition: {
      staggerChildren: staggerDelay,
    },
  }),
};

const letterVariants: Variants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1],
    },
  },
};

export const AnimatedText: React.FC<AnimatedTextProps> = ({
  children,
  animation = 'fadeUp',
  delay = 0,
  duration = 0.6,
  staggerDelay = 0.1,
  className = '',
  gradient = false,
  triggerOnView = true,
  repeatOnView = false,
  as = 'span',
  ...props
}) => {
  const MotionComponent = motion[as] as typeof motion.span;

  const gradientClass = gradient ? 'text-gradient-primary-animated' : '';

  // Simple animations (fadeUp, fadeIn, slideUp)
  if (animation === 'fadeUp' || animation === 'fadeIn' || animation === 'slideUp') {
    const variants = {
      fadeUp: fadeUpVariants,
      fadeIn: fadeInVariants,
      slideUp: slideUpVariants,
    }[animation];

    return (
      <MotionComponent
        className={cn(gradientClass, className)}
        variants={variants}
        initial="hidden"
        animate={triggerOnView ? undefined : 'visible'}
        whileInView={triggerOnView ? 'visible' : undefined}
        viewport={{ once: !repeatOnView, margin: '-50px' }}
        custom={delay}
        {...props}
      >
        {children}
      </MotionComponent>
    );
  }

  // Word-by-word animation
  if (animation === 'wordByWord') {
    const words = children.split(' ');

    return (
      <MotionComponent
        className={cn('inline-flex flex-wrap', gradientClass, className)}
        variants={wordContainerVariants}
        initial="hidden"
        animate={triggerOnView ? undefined : 'visible'}
        whileInView={triggerOnView ? 'visible' : undefined}
        viewport={{ once: !repeatOnView, margin: '-50px' }}
        custom={{ staggerDelay, delay }}
        aria-label={children}
        {...props}
      >
        {words.map((word, index) => (
          <motion.span
            key={`${word}-${index}`}
            className="inline-block mr-[0.25em]"
            variants={wordVariants}
          >
            {word}
          </motion.span>
        ))}
      </MotionComponent>
    );
  }

  // Letter-by-letter animation
  if (animation === 'letterByLetter') {
    const letters = children.split('');

    return (
      <MotionComponent
        className={cn('inline-flex', gradientClass, className)}
        variants={letterContainerVariants}
        initial="hidden"
        animate={triggerOnView ? undefined : 'visible'}
        whileInView={triggerOnView ? 'visible' : undefined}
        viewport={{ once: !repeatOnView, margin: '-50px' }}
        custom={staggerDelay}
        aria-label={children}
        {...props}
      >
        {letters.map((letter, index) => (
          <motion.span
            key={`${letter}-${index}`}
            className={letter === ' ' ? 'inline-block w-[0.25em]' : 'inline-block'}
            variants={letterVariants}
          >
            {letter === ' ' ? '\u00A0' : letter}
          </motion.span>
        ))}
      </MotionComponent>
    );
  }

  // Typewriter animation
  if (animation === 'typewriter') {
    return (
      <MotionComponent
        className={cn(gradientClass, className)}
        initial={{ width: 0 }}
        animate={triggerOnView ? undefined : { width: 'auto' }}
        whileInView={triggerOnView ? { width: 'auto' } : undefined}
        viewport={{ once: !repeatOnView, margin: '-50px' }}
        transition={{ duration: duration, ease: 'linear', delay }}
        style={{ overflow: 'hidden', whiteSpace: 'nowrap', display: 'inline-block' }}
        aria-label={children}
        {...props}
      >
        {children}
      </MotionComponent>
    );
  }

  // Default fallback
  return (
    <MotionComponent className={cn(gradientClass, className)} {...props}>
      {children}
    </MotionComponent>
  );
};

// Preset component for hero headlines
export const HeroHeadline: React.FC<Omit<AnimatedTextProps, 'as'> & { as?: 'h1' | 'h2' | 'h3' }> = ({
  children,
  as = 'h1',
  animation = 'wordByWord',
  staggerDelay = 0.08,
  className = '',
  ...props
}) => {
  return (
    <AnimatedText
      as={as}
      animation={animation}
      staggerDelay={staggerDelay}
      className={cn('text-5xl md:text-7xl font-black tracking-tight leading-tight', className)}
      {...props}
    >
      {children}
    </AnimatedText>
  );
};

// Preset component for section headings with scroll animation
export const AnimatedHeading: React.FC<Omit<AnimatedTextProps, 'as'> & { as?: 'h2' | 'h3' | 'h4' }> = ({
  children,
  as = 'h2',
  animation = 'fadeUp',
  delay = 0,
  className = '',
  ...props
}) => {
  return (
    <AnimatedText
      as={as}
      animation={animation}
      delay={delay}
      triggerOnView={true}
      className={className}
      {...props}
    >
      {children}
    </AnimatedText>
  );
};
